// Client wrappers over the Cloudflare Worker API.
//
// Two endpoints:
//   POST /api/upload-url        — authed, presigns an R2 PUT
//   GET  /api/community/samples — unauthed, lists public samples
//
// Both share the worker base URL from VITE_WORKER_URL.

import { getWorkerUrl } from './env';

export type PresignedUpload = {
  url: string;
  key: string;
  expiresIn: number;
};

export async function requestUploadUrl(params: {
  jwt: string;
  patchId: string;
  sampleId: string;
  contentType?: string;
}): Promise<PresignedUpload> {
  const base = getWorkerUrl();
  if (!base) throw new Error('VITE_WORKER_URL not configured');
  const res = await fetch(`${base}/api/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.jwt}`,
    },
    body: JSON.stringify({
      patchId: params.patchId,
      sampleId: params.sampleId,
      contentType: params.contentType,
    }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      `upload-url request failed (${res.status}): ${detail || res.statusText}`,
    );
  }
  return (await res.json()) as PresignedUpload;
}

// ── Community samples ─────────────────────────────────────────────

export type CommunityLicense = 'cc0' | 'cc-by' | 'self' | 'unspecified';

export type CommunitySample = {
  id: string;
  name: string;
  tags: string[];
  license: CommunityLicense;
  r2Key: string;
  durationMs: number;
  publishedAt: string;
  authorHandle: string;
};

export type CommunityListResult = {
  items: CommunitySample[];
  nextCursor: string | null;
};

export type CommunityListOptions = {
  q?: string;
  tag?: string;
  cursor?: string;
  limit?: number;
};

export async function listCommunitySamples(
  opts: CommunityListOptions = {},
): Promise<CommunityListResult> {
  const base = getWorkerUrl();
  if (!base) throw new Error('VITE_WORKER_URL not configured');
  const url = new URL(`${base}/api/community/samples`);
  if (opts.q) url.searchParams.set('q', opts.q);
  if (opts.tag) url.searchParams.set('tag', opts.tag);
  if (opts.cursor) url.searchParams.set('cursor', opts.cursor);
  if (opts.limit !== undefined) url.searchParams.set('limit', String(opts.limit));

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      `community samples request failed (${res.status}): ${detail || res.statusText}`,
    );
  }
  return (await res.json()) as CommunityListResult;
}

// ── Community patches (songs) ─────────────────────────────────────

export type CommunityPatch = {
  id: string;
  title: string;
  code: string;
  publishedAt: string;
  updatedAt: string;
  authorHandle: string;
  playsCount: number;
  forksCount: number;
};

export type CommunityPatchListResult = {
  items: CommunityPatch[];
  nextCursor: string | null;
};

export type CommunityPatchSort = 'new' | 'trending';

export type CommunityPatchListOptions = {
  q?: string;
  cursor?: string;
  limit?: number;
  /** 'trending' = plays over the last 7 days, single page. */
  sort?: CommunityPatchSort;
};

export async function listCommunityPatches(
  opts: CommunityPatchListOptions = {},
): Promise<CommunityPatchListResult> {
  const base = getWorkerUrl();
  if (!base) throw new Error('VITE_WORKER_URL not configured');
  const url = new URL(`${base}/api/community/patches`);
  if (opts.q) url.searchParams.set('q', opts.q);
  if (opts.cursor) url.searchParams.set('cursor', opts.cursor);
  if (opts.limit !== undefined) url.searchParams.set('limit', String(opts.limit));
  if (opts.sort) url.searchParams.set('sort', opts.sort);

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      `community patches request failed (${res.status}): ${detail || res.statusText}`,
    );
  }
  return (await res.json()) as CommunityPatchListResult;
}

export async function forkPatchRequest(params: {
  jwt: string;
  patchId: string;
}): Promise<{ forkPatchId: string; lineageMissing?: boolean }> {
  const base = getWorkerUrl();
  if (!base) throw new Error('VITE_WORKER_URL not configured');
  const res = await fetch(`${base}/api/patches/fork`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.jwt}`,
    },
    body: JSON.stringify({ patchId: params.patchId }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      `fork request failed (${res.status}): ${detail || res.statusText}`,
    );
  }
  return (await res.json()) as { forkPatchId: string; lineageMissing?: boolean };
}

export async function recordPlayRequest(patchId: string): Promise<void> {
  const base = getWorkerUrl();
  if (!base) throw new Error('VITE_WORKER_URL not configured');
  // Anonymous endpoint — no auth header. Fire-and-forget at the call
  // site (errors surface only if the caller explicitly awaits + catches).
  const res = await fetch(`${base}/api/patches/play`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ patchId }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      `play request failed (${res.status}): ${detail || res.statusText}`,
    );
  }
}
